// Paddle.js bootstrap + helpers for the international checkout.
import { supabase } from "@/integrations/supabase/client";

const clientToken = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;

declare global {
  interface Window {
    // deno-lint-ignore no-explicit-any
    Paddle: any;
  }
}

let paddleInitialized = false;
let initPromise: Promise<void> | null = null;

export function getPaddleEnv(): "sandbox" | "live" {
  return clientToken?.startsWith("test_") ? "sandbox" : "live";
}

export async function initializePaddle(): Promise<void> {
  if (paddleInitialized) return;
  if (initPromise) return initPromise;
  if (!clientToken) throw new Error("Payments not configured");

  initPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-paddle]");
    const onReady = () => {
      try {
        const env = clientToken.startsWith("test_") ? "sandbox" : "production";
        window.Paddle.Environment.set(env);
        window.Paddle.Initialize({ token: clientToken });
        paddleInitialized = true;
        resolve();
      } catch (e) {
        reject(e);
      }
    };

    if (existing && window.Paddle) return onReady();

    const script = existing ?? document.createElement("script");
    if (!existing) {
      script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
      script.dataset.paddle = "true";
      script.onload = onReady;
      script.onerror = () => reject(new Error("Paddle.js failed to load"));
      document.head.appendChild(script);
    }
  });

  return initPromise;
}

/** Server-side: create a Paddle transaction for the given pack. */
export async function createPaddleTransaction(packId: string): Promise<{
  transactionId: string;
  environment: "sandbox" | "live";
}> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error("Please sign in again");

  const { data, error } = await supabase.functions.invoke("create-paddle-checkout", {
    body: { packId, environment: getPaddleEnv() },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) throw error;
  if (!data?.transactionId) throw new Error("Checkout setup failed");
  return { transactionId: data.transactionId, environment: data.environment };
}
